import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { format, isToday, isPast, startOfDay } from 'date-fns';
import { DraggableActivity } from './DraggableActivity';
import { COLORS } from '@/data';
import type { ScheduledActivity, Clip } from '@/types';

interface DroppableCalendarCellProps {
  date: Date;
  activities: ScheduledActivity[];
  clips: Clip[];
  isCurrentMonth: boolean;
  onActivityClick?: (activity: ScheduledActivity) => void;
}

export const DroppableCalendarCell: React.FC<DroppableCalendarCellProps> = ({
  date,
  activities,
  clips,
  isCurrentMonth,
  onActivityClick,
}) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const isPastDate = isPast(startOfDay(date)) && !isToday(date);

  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${dateStr}`,
    data: {
      type: 'calendar-cell',
      date: dateStr,
    },
    disabled: isPastDate,
  });

  // Get activities for this date
  const dayActivities = activities.filter(a => a.scheduledDate === dateStr);
  const scheduledCount = dayActivities.filter(a => !a.completedAt).length;
  const completedCount = dayActivities.filter(a => a.completedAt).length;

  // Get display activities (max 2 shown, then "+N more")
  const displayActivities = dayActivities.slice(0, 2);
  const moreCount = dayActivities.length - 2;

  return (
    <div
      ref={setNodeRef}
      className={`
        relative min-h-24 md:min-h-28 lg:min-h-32 p-1.5 md:p-2
        border border-zinc-700/50 rounded-lg
        transition-all duration-150
        ${isCurrentMonth ? 'bg-zinc-800/30' : 'bg-zinc-900/30'}
        ${isPastDate ? 'opacity-40 cursor-not-allowed' : ''}
        ${isOver && !isPastDate ? 'bg-lime-500/20 border-lime-500 scale-[1.02]' : ''}
        ${isToday(date) ? 'ring-2 ring-lime-500/50' : ''}
      `}
    >
      {/* Date number */}
      <div className="flex items-center justify-between mb-1">
        <span
          className={`
            text-xs md:text-sm font-medium
            ${isCurrentMonth ? 'text-zinc-300' : 'text-zinc-600'}
            ${isToday(date) ? 'text-lime-400 font-bold' : ''}
          `}
        >
          {format(date, 'd')}
        </span>

        {/* Status indicators */}
        <div className="flex gap-0.5">
          {scheduledCount > 0 && (
            <div
              className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-orange-500"
              title={`${scheduledCount} scheduled`}
            />
          )}
          {completedCount > 0 && (
            <div
              className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-green-500"
              title={`${completedCount} completed`}
            />
          )}
        </div>
      </div>

      {/* Activities - now draggable */}
      <div className="space-y-0.5 md:space-y-1">
        {displayActivities.map(activity => {
          const clip = clips.find(c => c.id === activity.clipId);

          return (
            <DraggableActivity
              key={activity.id}
              activity={activity}
              clip={clip}
              onClick={() => onActivityClick?.(activity)}
            />
          );
        })}

        {moreCount > 0 && (
          <p className="text-xs text-zinc-500 px-1.5">+{moreCount} more</p>
        )}
      </div>

      {/* Drop zone indicator when dragging over */}
      {isOver && !isPastDate && (
        <div
          className="absolute inset-0 rounded-lg border-2 border-dashed pointer-events-none"
          style={{ borderColor: COLORS.lime }}
        />
      )}
    </div>
  );
};
